import { assertNever } from "@msys/common";
import { keyBy } from "lodash-es";
import { ItemTemplateSearchFilterPropertyFilterFragment } from "../templates/templateSearchFilters.generated.js";
import { Props2NonComputedAllFragment } from "./properties.generated.js";
import { isComputedSearchFilterSlot } from "./searchFilterSlots.js";

/**
 * Reconcile a placeholder's stored filter slots against a template type's
 * properties after the type changed. A filter whose property is gone is
 * dropped; one whose property changed to an incompatible kind is dropped when
 * authored, and kept with its expression commented out when expression-driven —
 * the source survives for the user to repair rather than vanishing.
 */
export function adjustTemplateSearchFilterPropertyFiltersToTemplateTypeProps2({
  filters,
  templateTypeProps,
}: {
  filters: ItemTemplateSearchFilterPropertyFilterFragment[];
  templateTypeProps: Props2NonComputedAllFragment[];
}): ItemTemplateSearchFilterPropertyFilterFragment[] {
  const templateTypePropsByKey = keyBy(templateTypeProps, p => p.key);

  return filters.flatMap(filter => {
    const templateTypeProp = templateTypePropsByKey[filter.key];
    if (!templateTypeProp) return [];

    if (requiredPropTypenameForFilter(filter) === templateTypeProp.__typename) {
      return [filter];
    }

    return isComputedSearchFilterSlot(filter) && "expr" in filter
      ? [{ ...filter, expr: commentedExpression(filter.expr) }]
      : [];
  });
}

/**
 * The slots to write back after a placeholder's template type changed. The type
 * change clears the stored slots in a mutation of its own, and the restoring
 * write gates its expression half separately — so a viewer without expression
 * access restores the authored rows rather than having the whole write
 * rejected and losing those too.
 */
export function templateSearchFilterPropertyFiltersToRestoreAfterTypeChange({
  filters,
  templateTypeProps,
  canWriteExpressions,
}: {
  filters: ItemTemplateSearchFilterPropertyFilterFragment[];
  templateTypeProps: Props2NonComputedAllFragment[];
  canWriteExpressions: boolean;
}): ItemTemplateSearchFilterPropertyFilterFragment[] {
  const adjusted =
    adjustTemplateSearchFilterPropertyFiltersToTemplateTypeProps2({
      filters,
      templateTypeProps,
    });

  return canWriteExpressions
    ? adjusted
    : adjusted.filter(filter => !isComputedSearchFilterSlot(filter));
}

/** The property kind a filter clause can address, whichever flavour it is. */
function requiredPropTypenameForFilter(
  filter: ItemTemplateSearchFilterPropertyFilterFragment
): Props2NonComputedAllFragment["__typename"] {
  switch (filter.__typename) {
    case "EntitySearchPropertyFilterBoolFilter":
    case "EntitySearchPropertyFilterBoolFilterComputed":
    case "EntitySearchPropertyFilterBoolInFilter":
    case "EntitySearchPropertyFilterBoolInFilterComputed":
      return "Props2Bool";
    case "EntitySearchPropertyFilterNumberFilter":
    case "EntitySearchPropertyFilterNumberFilterComputed":
    case "EntitySearchPropertyFilterNumberInFilter":
    case "EntitySearchPropertyFilterNumberInFilterComputed":
    case "EntitySearchPropertyFilterNumberBetweenFilter":
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
      return "Props2Number";
    case "EntitySearchPropertyFilterNumberArrayOfFilter":
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed":
      return "Props2NumberArray";
    case "EntitySearchPropertyFilterTextFilter":
    case "EntitySearchPropertyFilterTextFilterComputed":
    case "EntitySearchPropertyFilterTextInFilter":
    case "EntitySearchPropertyFilterTextInFilterComputed":
      return "Props2Text";
    case "EntitySearchPropertyFilterTextArrayOfFilter":
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed":
      return "Props2TextArray";
    default:
      return assertNever(filter);
  }
}

function commentedExpression(expr: string) {
  return `__NULL__ /** ${expr.replace(/\*\//g, "")} */`;
}
